<template>
	<el-dialog
		v-model="visible"
		:title="$t('uChoicePc.forgotPassword')"
		width="648px"
		:close-on-click-modal="false"
		:show-close="true"
		class="forgot-password-dialog"
	>
		<el-form ref="formRef" :model="formData" :rules="formRules" size="large" label-position="top" class="space-y-4 p-6">
			<!-- 用户名（邮箱） -->
			<el-form-item :label="$t('uChoicePc.verificationMethod')" prop="accountType" class="required-item">
				<el-select v-model="formData.accountType" size="large" :placeholder="$t('uChoicePc.pleaseSelect')">
					<el-option :label="$t('uChoicePc.email')" :value="1"></el-option>
					<el-option :label="$t('uChoicePc.phone')" :value="0"></el-option>
				</el-select>
			</el-form-item>
			<el-form-item :label="formData.accountType === 0 ? $t('uChoicePc.phone') : $t('uChoicePc.email')" prop="account" class="required-item">
				<div class="flex-1">
					<el-input v-model="formData.account" :placeholder="$t('uChoicePc.pleaseFillIn')" class="input_item_box flex-1 w-[100%] bg-[#F9F8F8]">
						<template #prepend v-if="formData.accountType === 0">
							<el-select v-model="formData.phoneCountryCode" class="w-20">
								<el-option v-for="item in countryCodes" :key="item.code" :label="item.code" :value="item.code" />
							</el-select>
						</template>
					</el-input>
				</div>
			</el-form-item>

			<!-- 验证码 -->
			<el-form-item :label="$t('uChoicePc.verifyCode')" prop="verificationCode" class="required-item relative">
				<el-input v-model="formData.verificationCode" :placeholder="$t('uChoicePc.pleaseFillIn')" class="rounded-2xl">
					<template #suffix>
						<el-button class="bg-transparent hover:bg-transparent" text :disabled="countdown > 0" @click="handleGetCode">
							<span :class="[countdown === 0 ? 'text-[#F92863]' : '']">
								{{ countdown > 0 ? `${countdown}${$t('uChoicePc.countdownText')}` : $t('uChoicePc.getCode') }}</span
							>
						</el-button>
					</template>
				</el-input>
			</el-form-item>

			<!-- 新密码 -->
			<el-form-item :label="$t('uChoicePc.password')" prop="password" class="required-item">
				<el-input v-model="formData.password" type="password" :placeholder="$t('uChoicePc.pleaseFillIn')" class="rounded-2xl"></el-input>
			</el-form-item>

			<!-- 再次确认新密码 -->
			<el-form-item :label="$t('uChoicePc.confirmNewPassword')" prop="confirmPassword" class="required-item">
				<el-input v-model="formData.confirmPassword" type="password" :placeholder="$t('uChoicePc.pleaseFillIn')" class="rounded-2xl"></el-input>
			</el-form-item>
		</el-form>

		<template #footer>
			<div class="flex justify-center gap-4 pt-2">
				<el-button class="w-[160px] h-[54px] text-[18px] rounded-2xl bg-gray-100 text-gray-800 border-0 hover:bg-gray-200" @click="handleCancel">
					{{ $t('uChoicePc.cancel') }}
				</el-button>
				<el-button type="primary" class="w-[160px] h-[54px] text-[18px] rounded-2xl bg-[#F92863] hover:bg-[#D8004E] border-0" @click="handleSubmit">
					{{ $t('uChoicePc.submit') }}
				</el-button>
			</div>
		</template>
	</el-dialog>
</template>

<script setup lang="ts">
import { ref, reactive, onBeforeUnmount } from 'vue'
import { ElForm, ElMessage } from 'element-plus'
import { fetForgetCode, resetPwd, getSelectorNoAuth } from '@/api/mall/index'

// 引入全局i18n实例
import lang from '@/lang/index'
const i18n = lang.global

// 弹窗显隐控制
const visible = ref(false)

// 表单引用
const formRef = ref<InstanceType<typeof ElForm>>()

// 倒计时
const countdown = ref(0)
let countdownTimer: number | null = null

// 表单数据
const formData = ref({
	email: '',
	account: '',
	accountType: 1, //账号类型(0手机号，1邮箱)
	confirmPassword: '',
	password: '',
	phoneCountryCode: '',
	verificationCode: ''
})

// 表单校验规则：全部使用 i18n.t()
const formRules = reactive({
	account: [
		{
			required: true,
			message: i18n.t('uChoicePc.email') + i18n.t('uChoicePc.required'),
			trigger: 'blur'
		}
	],
	verificationCode: [
		{
			required: true,
			message: i18n.t('uChoicePc.verifyCode') + i18n.t('uChoicePc.required'),
			trigger: 'blur'
		}
	],
	password: [
		{
			required: true,
			message: i18n.t('uChoicePc.password') + i18n.t('uChoicePc.required'),
			trigger: 'blur'
		}
	],
	confirmPassword: [
		{
			required: true,
			message: i18n.t('uChoicePc.confirmNewPassword') + i18n.t('uChoicePc.required'),
			trigger: 'blur'
		},
		{
			validator: (rule: any, value: string, callback: any) => {
				if (value !== formData.value.password) {
					callback(new Error(i18n.t('uChoicePc.passwordNotMatch')))
				} else {
					callback()
				}
			},
			trigger: 'blur'
		}
	]
})

// 打开弹窗
const openDialog = () => {
	visible.value = true
	formRef.value?.resetFields()
	countdown.value = 0
	getSysType()
}

// 关闭弹窗
const handleCancel = () => {
	visible.value = false
}

// 获取验证码
const handleGetCode = () => {
	formRef.value?.validateField('account', (validate) => {
		if (validate) {
			fetForgetCode(formData.value)
				.then(() => {
					// 成功后启动倒计时
					countdown.value = 60
					countdownTimer = window.setInterval(() => {
						countdown.value--
						if (countdown.value <= 0) {
							countdownTimer && clearInterval(countdownTimer)
						}
					}, 1000)
				})
				.catch(() => {
					ElMessage.error(i18n.t('uChoicePc.networkError'))
				})
		}
	})
}

const countryCodes = ref<any[]>([])
// 获取国家编码
const getSysType = () => {
	const params: any = {
		type: 'DICT_DATA',
		business: 'countryCode'
	}
	getSelectorNoAuth(params).then((res: any) => {
		countryCodes.value = res.data || []
	})
}
// 提交表单
const handleSubmit = () => {
	formRef.value?.validate((valid) => {
		if (valid) {
			resetPwd(formData.value)
				.then(() => {
					ElMessage.success(i18n.t('uChoicePc.passwordResetSuccess'))
					visible.value = false
				})
				.catch(() => {
					ElMessage.error(i18n.t('uChoicePc.networkError'))
				})
		}
	})
}

defineExpose({
	openDialog
})
onBeforeUnmount(() => {
	if (countdownTimer) clearInterval(countdownTimer)
})
</script>

<style scoped>
.forgot-password-dialog :deep(.el-dialog__header) {
	padding: 16px 20px;
	border-bottom: 1px solid #f0f0f0;
}
.forgot-password-dialog :deep(.el-dialog__title) {
	font-size: 18px;
	font-weight: bold;
}
.forgot-password-dialog :deep(.el-dialog__body) {
	padding: 20px;
}
.forgot-password-dialog :deep(.el-dialog__footer) {
	padding: 10px 20px 20px;
	border-top: 1px solid #f0f0f0;
}

.required-item :deep(.el-input) {
	height: 46px;
}

.required-item :deep(.el-select--large .el-select__wrapper) {
	padding: 16px;
}
</style>
